import { useMemo } from 'react'
import { connectedIssueTracker, isCodingAgentConnected } from '../lib/agentConnection'
import { useGetMonitoringStatusQuery } from '../services/sessionMonitoringApi'
import { isSelfHealingSetUp } from '../schemas/sessionMonitoring'
import { useGetProjectQuery, useGetSecurityOwnershipQuery, useGetTestingSchedulesQuery } from '../services/projectDataApi'
import { testingScheduleSummary } from '../lib/testingScheduleSummary'
import { isGithubConnected } from '../lib/githubTesting'
import type { SupportStatus } from './SupportCard'

const loading: SupportStatus = { tone: 'yellow', text: 'Checking status…' }
const unavailable: SupportStatus = { tone: 'yellow', text: 'Status unavailable — open to retry' }
const setup: SupportStatus = { tone: 'red', text: 'Not configured', setup: true }
const disabled: SupportStatus = { tone: 'gray', text: 'Disabled' }

export type SetupItemKey = 'schedule' | 'github' | 'security' | 'agent' | 'healing' | 'tracker'
/** done: nothing left to do. waiting: configured, waiting on something outside QA (first session). off:
 *  configured once, then turned off. todo: never configured. unknown: still loading or the query failed. */
export type SetupItemState = 'done' | 'waiting' | 'off' | 'todo' | 'unknown'

export interface ProjectSetupStatus {
  canEdit: boolean
  /** The legacy banner's completion bar: sessions arriving, a schedule on, and security ownership resolved. */
  bannerComplete: boolean
  project: ReturnType<typeof useGetProjectQuery>['currentData']
  trackerName: string | null
  cards: Record<SetupItemKey, SupportStatus>
  states: Record<SetupItemKey, SetupItemState>
}

export function useProjectSetupStatus(projectId: string, { withScheduleSummary = true } = {}): ProjectSetupStatus {
  const monitoring = useGetMonitoringStatusQuery(projectId, { pollingInterval: 30000, skipPollingIfUnfocused: true })
  const schedules = useGetTestingSchedulesQuery(projectId, { pollingInterval: 60000, skipPollingIfUnfocused: true })
  const projectQuery = useGetProjectQuery(projectId, { pollingInterval: 15000, skipPollingIfUnfocused: true })
  const project = projectQuery.currentData
  const securityEnabled = project?.enabled_polish_passes?.includes('security') === true
  const exempt = project?.security_ownership_exempt === true
  const ownership = useGetSecurityOwnershipQuery({ projectId, targetUrl: project?.target_url ?? '', nonce: project?.security_nonce ?? '' }, {
    skip: !project?.security_nonce || exempt || !securityEnabled,
    pollingInterval: 15000, skipPollingIfUnfocused: true,
  })
  const status = monitoring.currentData
  const savedSchedules = schedules.currentData?.schedules
  const currentMinute = Math.floor(Date.now() / 60000)
  const scheduleSummary = useMemo(() => !withScheduleSummary || !savedSchedules ? ''
    : testingScheduleSummary(savedSchedules, new Date(currentMinute * 60000)), [savedSchedules, currentMinute, withScheduleSummary])

  const scheduleOn = savedSchedules?.some(schedule => schedule.config.enabled) === true
  const ownershipVerified = exempt || ownership.currentData?.latest?.verified === true
  const bannerComplete = Boolean(project && status?.has_sessions && scheduleOn && (!securityEnabled || ownershipVerified))

  const healing: SupportStatus = monitoring.isError ? unavailable : !status ? loading
    : !isSelfHealingSetUp(status) ? { tone: 'red', text: 'Watch user sessions for bugs', setup: true } : !status.enabled ? disabled
    : !status.has_sessions ? { tone: 'yellow', text: 'Waiting for the first session' }
    : { tone: 'white', text: `${status.sessions_last_24h.toLocaleString()} sessions · ${status.bugs_last_24h.toLocaleString()} bugs in the last 24 hours` }
  const testing: SupportStatus = schedules.isError ? unavailable : !savedSchedules ? loading
    : savedSchedules.length === 0 ? setup : !scheduleOn ? disabled
    : { tone: 'white', text: scheduleSummary }
  const ownershipRequired: SupportStatus = { tone: 'red', text: 'Confirm ownership to view security findings' }
  const activeSecurity: SupportStatus = monitoring.isError ? unavailable : !status ? loading
    : { tone: 'white', text: `${status.open_security_bugs.toLocaleString()} open security bugs` }
  const security: SupportStatus = projectQuery.isError ? unavailable : !project ? loading
    : !securityEnabled ? disabled
    : !exempt && !project.security_nonce ? ownershipRequired
    : exempt ? activeSecurity
    : ownership.isError ? unavailable : !ownership.currentData ? loading
    : !ownership.currentData.latest?.verified ? ownershipRequired
    : activeSecurity
  const tracker = project ? connectedIssueTracker(project) : null
  const agentConnected = project ? isCodingAgentConnected(project) : false
  const agent: SupportStatus = projectQuery.isError ? unavailable : !project ? loading
    : agentConnected ? { tone: 'white', text: `Connected, ${project.agent_read_bug_count ?? 0} bugs read, ${project.resolved_bug_count ?? 0} resolved` }
    : tracker ? { tone: 'gray', text: `Not connected, bugs go to ${tracker.name}` }
    : { tone: 'red', text: 'Agent not connected' }
  const issueTracker: SupportStatus = projectQuery.isError ? unavailable : !project ? loading
    : tracker ? { tone: 'white', text: `${tracker.name} connected, ${project.issue_tracker_filed_bug_count ?? 0} bugs filed, ${project.resolved_bug_count ?? 0} resolved` }
    : { tone: 'gray', text: 'Send new bugs to GitHub Issues, GitLab, Linear, Jira, or Asana' }
  const githubConnected = isGithubConnected(project?.github)
  const github: SupportStatus = projectQuery.isError ? unavailable : !project ? loading
    : githubConnected ? { tone: 'white', text: `Connected to ${project.github!.repoUrl.replace(/^https?:\/\/(www\.)?github\.com\//, '')}` }
    : project.github?.repoUrl ? { tone: 'yellow', text: 'Verify GitHub access to finish connecting' }
    : { tone: 'red', text: 'Not connected', setup: true }

  const isUnknown = (card: SupportStatus) => card === loading || card === unavailable
  const states: Record<SetupItemKey, SetupItemState> = {
    schedule: isUnknown(testing) ? 'unknown' : scheduleOn ? 'done' : testing === disabled ? 'off' : 'todo',
    github: isUnknown(github) ? 'unknown' : githubConnected ? 'done' : 'todo',
    security: isUnknown(security) ? 'unknown' : security === activeSecurity ? 'done' : !securityEnabled ? 'off' : 'todo',
    // A connected tracker already receives every bug, so the agent step doesn't hold up setup.
    agent: isUnknown(agent) ? 'unknown' : agentConnected || tracker ? 'done' : 'todo',
    healing: isUnknown(healing) ? 'unknown' : healing.tone === 'white' ? 'done' : healing.tone === 'yellow' ? 'waiting' : healing === disabled ? 'off' : 'todo',
    tracker: isUnknown(issueTracker) ? 'unknown' : tracker ? 'done' : 'todo',
  }

  return {
    canEdit: status?.can_edit === true,
    bannerComplete,
    project,
    trackerName: tracker?.name ?? null,
    cards: { schedule: testing, github, security, agent, healing, tracker: issueTracker },
    states,
  }
}
